
import React, { useState, useEffect } from 'react';
import { Zap, Server, Shield, Database, Plus, CheckCircle2, X, Trash2, AlertCircle } from 'lucide-react';
import { API_BASE, MCP_BASE } from '../../utils/api';

interface MCPTool {
  name: string;
  description: string;
  isActive?: boolean;
}

interface CustomTool extends MCPTool {
  id: string;
}

interface ServiceAccountInfo {
  path: string;
  project: string;
  location: string;
  isValid: boolean;
  error?: string;
}

// Model options matching agent builder
const MODEL_OPTIONS: Record<string, { models: string[]; supportsServiceAccount: boolean }> = {
  'Google Vertex AI': {
    models: [
      'gemini-3-pro-preview',
      'gemini-3-flash-preview',
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
      'gemini-2.5-pro',
      'gemini-2.0-flash',
      'gemini-2.0-flash-lite'
    ],
    supportsServiceAccount: true
  },
  'Google AI Studio': {
    models: [
      'gemini-3-pro-preview',
      'gemini-3-flash-preview',
      'gemini-2.5-flash',
      'gemini-2.5-pro'
    ],
    supportsServiceAccount: false
  },
  'OpenAI': {
    models: [
      'openai:gpt-5.2',
      'openai:gpt-5-mini',
      'openai:gpt-5-nano',
      'openai:gpt-4.1',
      'openai:gpt-4.1-mini',
      'openai:gpt-4.1-nano',
      'openai:o3',
      'openai:o3-mini',
      'openai:o4-mini',
      'openai:gpt-4o',
      'openai:gpt-4o-mini'
    ],
    supportsServiceAccount: false
  },
  'Anthropic': {
    models: [
      'claude-3-7-sonnet-20250219',
      'claude-3-5-sonnet-20241022',
      'claude-3-opus-20240229',
      'claude-3-sonnet-20240229'
    ],
    supportsServiceAccount: false
  }
};

const MCP_SERVER_URL = MCP_BASE;

const ModelsTools: React.FC = () => {
  const [availableTools, setAvailableTools] = useState<MCPTool[]>([]);
  const [customTools, setCustomTools] = useState<CustomTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [activatedTools, setActivatedTools] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [mcpServerUrl, setMcpServerUrl] = useState(MCP_SERVER_URL);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customToolName, setCustomToolName] = useState('');
  const [customToolDescription, setCustomToolDescription] = useState('');
  const [serviceAccountInfo, setServiceAccountInfo] = useState<ServiceAccountInfo | null>(null);
  const [checkingServiceAccount, setCheckingServiceAccount] = useState(false);
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState('');

  // Model Registry state
  const [connectedModels, setConnectedModels] = useState<Array<{ id: string; provider: string; model: string; isDefault: boolean }>>([
    { id: '1', provider: 'Google Vertex AI', model: 'gemini-3-pro-preview', isDefault: true }
  ]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [newModelProvider, setNewModelProvider] = useState('Google Vertex AI');
  const [newModelName, setNewModelName] = useState('gemini-3-pro-preview');

  const fetchTools = async (url: string) => {
    setLoading(true);
    setConnectionStatus('connecting');
    setConnectionError('');
    try {
      const response = await fetch(`${API_BASE}/agent-builder/mcp/tools?url=${encodeURIComponent(url)}`);
      if (response.ok) {
        const tools = await response.json();
        setAvailableTools(tools);
        setConnectionStatus('connected');
      } else {
        const errData = await response.text();
        setConnectionStatus('error');
        setConnectionError(errData || 'Failed to fetch tools');
        // Fallback tools on error
        setAvailableTools([
          { name: 'analyze_file_with_prompt', description: 'Analyze files with custom prompts' },
          { name: 'fetch_from_mail', description: 'Fetch emails from mailbox' },
          { name: 'file_upload', description: 'Upload files to storage' },
          { name: 'generate_job_posting', description: 'Generate job postings from templates' },
          { name: 'match_resumes', description: 'Match resumes to job requirements' },
        ]);
      }
    } catch (error: any) {
      console.error('Error fetching MCP tools:', error);
      setConnectionStatus('error');
      setConnectionError(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch MCP tools on mount
  useEffect(() => {
    fetchTools(mcpServerUrl);
  }, []);

  // Load activated tools from localStorage and check service account on mount
  useEffect(() => {
    const saved = localStorage.getItem('activatedMCPTools');
    if (saved) {
      try {
        setActivatedTools(new Set(JSON.parse(saved)));
      } catch (e) {
        console.error('Error loading activated tools:', e);
      }
    }
    checkServiceAccountStatus();
  }, []);

  const checkServiceAccountStatus = async () => {
    setCheckingServiceAccount(true);
    try {
      const response = await fetch(`${API_BASE}/agent-builder/service-account`);
      if (response.ok) {
        const info: ServiceAccountInfo = await response.json();
        setServiceAccountInfo(info);
      } else {
        setServiceAccountInfo({
          path: '',
          project: '',
          location: '',
          isValid: false,
          error: 'Service account verification failed'
        });
      }
    } catch (error) {
      console.error('Error checking service account:', error);
      setServiceAccountInfo({
        path: '',
        project: '',
        location: '',
        isValid: false,
        error: 'Backend not reachable'
      });
    } finally {
      setCheckingServiceAccount(false);
    }
  };

  // Get tool type from name or description
  const getToolType = (tool: MCPTool): string => {
    if (tool.description.includes('Communication')) return 'Communication';
    if (tool.description.includes('Data Access')) return 'Data Access';
    if (tool.description.includes('Utility')) return 'Utility';
    return 'Tool';
  };

  const toggleToolSelection = (toolName: string) => {
    const newSet = new Set(selectedTools);
    if (newSet.has(toolName)) {
      newSet.delete(toolName);
    } else {
      newSet.add(toolName);
    }
    setSelectedTools(newSet);
  };

  const clearSelection = () => {
    setSelectedTools(new Set());
  };

  const toggleToolActivation = (toolName: string) => {
    const newSet = new Set(activatedTools);
    if (newSet.has(toolName)) {
      newSet.delete(toolName);
      setToastMessage({ text: `${toolName} deactivated`, type: 'success' });
    } else {
      newSet.add(toolName);
      setToastMessage({ text: `${toolName} activated`, type: 'success' });
    }
    setActivatedTools(newSet);
    localStorage.setItem('activatedMCPTools', JSON.stringify(Array.from(newSet)));
    setTimeout(() => setToastMessage(null), 3000);
  };

  const addCustomTool = async () => {
    if (customToolName.trim()) {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/agent-builder/mcp/tools/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: customToolName,
            description: customToolDescription || 'Custom tool',
          }),
        });

        if (response.ok) {
          setToastMessage({ text: `Tool ${customToolName} created successfully!`, type: 'success' });
          setCustomToolName('');
          setCustomToolDescription('');
          setShowAddCustom(false);
          // Refetch tools to show the new one
          const responseTools = await fetch(`${API_BASE}/agent-builder/mcp/tools?url=${encodeURIComponent(mcpServerUrl)}`);
          if (responseTools.ok) {
            setAvailableTools(await responseTools.json());
          }
        } else {
          const errorData = await response.json();
          setToastMessage({ text: `Error: ${errorData.detail || 'Failed to create tool'}`, type: 'error' });
        }
      } catch (error) {
        console.error('Error creating custom tool:', error);
        setToastMessage({ text: 'Failed to reach backend', type: 'error' });
      } finally {
        setLoading(false);
        setTimeout(() => setToastMessage(null), 3000);
      }
    }
  };

  const removeCustomTool = (id: string, toolName: string) => {
    setCustomTools(customTools.filter(t => t.id !== id));
    const newSet = new Set(selectedTools);
    newSet.delete(toolName);
    setSelectedTools(newSet);
  };

  const addConnectedModel = () => {
    if (newModelProvider && newModelName) {
      const newModel = {
        id: Math.random().toString(36).substr(2, 9),
        provider: newModelProvider,
        model: newModelName,
        isDefault: connectedModels.length === 0 // First model is default
      };
      setConnectedModels([...connectedModels, newModel]);
      setShowConnectModal(false);
      setNewModelProvider('Google Vertex AI');
      setNewModelName('gemini-3-pro-preview');
    }
  };

  const removeConnectedModel = (id: string) => {
    const remaining = connectedModels.filter(m => m.id !== id);
    // Ensure at least one is default
    if (remaining.length > 0 && !remaining.some(m => m.isDefault)) {
      remaining[0].isDefault = true;
    }
    setConnectedModels(remaining);
  };

  const setDefaultModel = (id: string) => {
    setConnectedModels(connectedModels.map(m => ({
      ...m,
      isDefault: m.id === id
    })));
  };

  const selectedToolsArray = Array.from(selectedTools);
  const allTools = [...availableTools, ...customTools];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Models & Tools</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Configure global LLMs and register custom tool capabilities.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Model Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="text-amber-500" size={20} />
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Model Registry</h2>
            </div>
            <button
              onClick={() => setShowConnectModal(true)}
              className="text-blue-600 text-sm font-bold hover:underline"
            >
              + Connect Model
            </button>
          </div>

          {/* Service Account Status */}
          <div className={`p-3 rounded-lg border text-xs font-medium transition-colors ${checkingServiceAccount
            ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
            : serviceAccountInfo?.isValid
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400'
            }`}>
            <div className="flex items-start gap-2">
              {checkingServiceAccount ? (
                <>
                  <svg className="animate-spin h-4 w-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Checking service account...</span>
                </>
              ) : serviceAccountInfo?.isValid ? (
                <>
                  <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="block font-bold">✓ Service account configured</span>
                    {serviceAccountInfo.project && (
                      <span className="block text-[11px] opacity-90">Project: {serviceAccountInfo.project}</span>
                    )}
                    {serviceAccountInfo.location && (
                      <span className="block text-[11px] opacity-90">Location: {serviceAccountInfo.location}</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="block font-bold">⚠ Service account not found</span>
                    <span className="block text-[11px] opacity-90">Place service account JSON in backend services folder</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {connectedModels.map((modelConfig) => {
              const latencyMap: Record<string, string> = {
                'Google Vertex AI': '240ms',
                'Google AI Studio': '280ms',
                'OpenAI': '480ms',
                'Anthropic': '350ms'
              };
              const latency = latencyMap[modelConfig.provider] || '300ms';
              return (
                <div key={modelConfig.id} className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                      <Server size={20} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">{modelConfig.model}</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{modelConfig.provider}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-bold text-slate-700">{latency}</p>
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Latency</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {modelConfig.isDefault && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700">
                          DEFAULT
                        </span>
                      )}
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 text-blue-700">
                        ACTIVE
                      </span>
                      {connectedModels.length > 1 && (
                        <button
                          onClick={() => removeConnectedModel(modelConfig.id)}
                          className="ml-2 text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Connect Model Modal */}
          {showConnectModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Connect New Model</h3>
                  <button
                    onClick={() => setShowConnectModal(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-2 uppercase">Provider</label>
                  <select
                    value={newModelProvider}
                    onChange={(e) => {
                      setNewModelProvider(e.target.value);
                      setNewModelName(MODEL_OPTIONS[e.target.value]?.models[0] || '');
                    }}
                    className="w-full text-sm px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                  >
                    {Object.keys(MODEL_OPTIONS).map(provider => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-2 uppercase">Model</label>
                  <select
                    value={newModelName}
                    onChange={(e) => setNewModelName(e.target.value)}
                    className="w-full text-sm px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                  >
                    <option value="" disabled>Select Model</option>
                    {MODEL_OPTIONS[newModelProvider]?.models.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={addConnectedModel}
                    className="flex-1 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Connect
                  </button>
                  <button
                    onClick={() => setShowConnectModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-bold text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Tool / MCP Registry Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="text-blue-500" size={20} />
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Tool / MCP Registry</h2>
            </div>
            <button
              onClick={() => setShowAddCustom(!showAddCustom)}
              className="text-blue-600 text-sm font-bold hover:underline"
            >
              + New Tool
            </button>
          </div>

          {/* MCP Server URL Configuration */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-lg p-3 space-y-3 transition-colors">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2 block">MCP Server URL</label>
                <input
                  type="text"
                  className="w-full text-xs px-3 py-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800/50 text-slate-900 dark:text-white rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
                  value={mcpServerUrl}
                  onChange={(e) => {
                    setMcpServerUrl(e.target.value);
                    setConnectionStatus('idle');
                  }}
                  placeholder={MCP_SERVER_URL}
                />
              </div>
              <button
                onClick={() => fetchTools(mcpServerUrl)}
                disabled={loading}
                className={`mb-[1px] px-4 py-2 text-xs font-bold rounded transition-all ${connectionStatus === 'connected'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                  } disabled:opacity-50`}
              >
                {loading ? '...' : connectionStatus === 'connected' ? 'Connected' : 'Connect'}
              </button>
            </div>

            {/* Connection Status Indicator */}
            {connectionStatus !== 'idle' && (
              <div className={`text-[10px] p-2 rounded flex items-start gap-2 font-medium ${connectionStatus === 'connecting' ? 'bg-white/50 text-slate-500' :
                connectionStatus === 'connected' ? 'bg-emerald-100/50 text-emerald-700' :
                  'bg-red-50 text-red-700'
                }`}>
                {connectionStatus === 'connecting' ? (
                  <svg className="animate-spin h-3 w-3 mt-0.5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : connectionStatus === 'connected' ? (
                  <CheckCircle2 size={14} className="mt-0.5" />
                ) : (
                  <AlertCircle size={14} className="mt-0.5" />
                )}
                <span>
                  {connectionStatus === 'connecting' && 'Connecting to MCP Server...'}
                  {connectionStatus === 'connected' && `Connected. ${availableTools.length} tools loaded.`}
                  {connectionStatus === 'error' && `Failed: ${connectionError}`}
                </span>
              </div>
            )}
          </div>

          {/* Add Custom Tool Form */}
          {showAddCustom && (
            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 space-y-3 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Add Custom Tool</h3>
                <button
                  onClick={() => setShowAddCustom(false)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Tool Name *</label>
                <input
                  type="text"
                  className="w-full text-xs px-3 py-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-blue-200 dark:border-blue-800/50 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
                  placeholder="e.g., Email Connector"
                  value={customToolName}
                  onChange={(e) => setCustomToolName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCustomTool()}
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Description</label>
                <textarea
                  className="w-full text-xs px-3 py-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-blue-200 dark:border-blue-800/50 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
                  placeholder="e.g., Send and receive emails"
                  rows={2}
                  value={customToolDescription}
                  onChange={(e) => setCustomToolDescription(e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={addCustomTool}
                  disabled={!customToolName.trim()}
                  className="flex-1 px-3 py-2 text-xs font-bold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-slate-300 transition-colors"
                >
                  Add Tool
                </button>
                <button
                  onClick={() => setShowAddCustom(false)}
                  className="flex-1 px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Selected Tools Indicator */}
          {selectedToolsArray.length > 0 && (
            <div className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 px-3 py-2 rounded border border-orange-200 dark:border-orange-800/50 transition-colors">
              <span className="text-xs text-orange-700 dark:text-orange-400 font-bold">{selectedToolsArray.length} tool(s) selected</span>
              <button
                onClick={clearSelection}
                className="text-[10px] text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 font-bold hover:underline transition-colors"
              >
                Clear all
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-xs">Loading tools...</span>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto pr-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allTools.map((tool, i) => {
                  const isSelected = selectedTools.has(tool.name);
                  const isActive = activatedTools.has(tool.name);
                  const isCustom = customTools.some(t => t.name === tool.name);
                  return (
                    <div
                      key={i}
                      className={`bg-white dark:bg-slate-800 p-5 rounded-2xl border-2 shadow-sm hover:shadow-md transition-all group cursor-pointer relative ${isSelected
                        ? 'border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-orange-300 dark:hover:border-orange-400/50'
                        }`}
                    >
                      {/* Remove Custom Tool Button */}
                      {isCustom && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCustomTool((tool as CustomTool).id, tool.name);
                          }}
                          className="absolute top-2 right-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete custom tool"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}

                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleToolActivation(tool.name);
                          }}
                          title={isActive ? 'Deactivate tool' : 'Activate tool'}
                          className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-all ${isActive
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-800/50'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                            }`}
                        >
                          <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-600 dark:bg-emerald-500' : 'bg-slate-400 dark:bg-slate-500'}`} />
                          {isActive ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                      <h4 className={`text-sm font-bold mb-1 text-slate-900 dark:text-white`}>
                        {tool.name}
                        {isCustom && <span className="ml-1 text-[10px] font-normal text-blue-600 dark:text-blue-400">[Custom]</span>}
                      </h4>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tight mb-4">{getToolType(tool)}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-3 border-t border-slate-50 dark:border-slate-700/50">
                        <span>Status</span>
                        <span className={`font-bold ${activatedTools.has(tool.name) ? 'text-emerald-600 dark:text-emerald-500' : ''}`}>
                          {activatedTools.has(tool.name) ? 'LIVE' : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected Tools Summary */}
          {selectedToolsArray.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/50 rounded-lg p-3 transition-colors">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">Selected Tools ({selectedToolsArray.length})</p>
              <div className="flex flex-wrap gap-2">
                {selectedToolsArray.map(toolName => (
                  <span key={toolName} className="px-2 py-1 bg-orange-200 dark:bg-orange-800/40 text-orange-900 dark:text-orange-300 text-[10px] font-semibold rounded-full flex items-center gap-1.5 border border-orange-300/50 dark:border-orange-700/50">
                    {toolName}
                    <button
                      onClick={() => toggleToolSelection(toolName)}
                      className="text-orange-700 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-200 font-bold transition-colors"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg text-sm font-bold text-white transition-all ${toastMessage.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
          }`}>
          {toastMessage.text}
        </div>
      )}
    </div>
  );
};

export default ModelsTools;
